/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import * as _ from 'lodash';

import { ScrollAwareContext, ScrollAwareScroller, ScrollAwareItem } from '../../../components/ScrollAware';
import { getLocalText } from '../../../../utilities/localizedUtil';
import ShareCardHealthEntryGroup, { ShareCardHealthEntryAllergyGroup } from './ShareCardElements/ShareCardHealthEntryGroup';
import ShareCardHealthEntryValueGroup, { ShareCardTrackableGroup } from './ShareCardElements/ShareCardHealthEntryValueGroup';
import PublicShareCardFooter from './PublicShareCardFooter';
import PublicShareCardControls from './PublicShareCardControls';

import './PublicShareCard.scss';
import HealthEntryCategoryNames from '../../../../constants/HealthEntryCategoryNames';
import healthEntryUtility from '../../../../models/HealthEntryUtility';
import { generalHealthEntry } from '../../../../models/HealthEntryCompose';
import ShareCardProfileGroup from './ShareCardElements/ShareCardProfileGroup';
import ShareCardGroupContents from './ShareCardElements/ShareCardGroupContents';
import { ShareCardFilesGroup } from './ShareCardElements/ShareCardFilesGroup';
import { ShareCardNotesGroup } from './ShareCardElements/ShareCardNotesGroup';
import { ShareCardContactsGroup } from './ShareCardElements/ShareCardContactsGroup';
import { ShareCardColorModel } from '../../../appAuthorized/dataTransformComponents/ColorShareCard';
import { ShareCardCommentGroup } from './ShareCardElements/ShareCardCommentGroup';
import { ShareCardDisclaimer } from './ShareCardElements/ShareCardDisclaimer';
import { I18nImage } from '../../../components/I18nTranslation';
import ShareCardIncludedTypeNames from '../../../../constants/ShareCardIncludedTypeNames';
import { useUIState } from '../../../hooks/useUIState';
import ProfileName from '../../../appAuthorized/views/profile/ProfileName';
import ProfileModel from '../../../../models/ProfileModel';
import ShareCardModel from '../../../../models/ShareCardModel';

interface Props {
  hash: string;
  metadata: any;
  languageCode: string;
  requiresPasscode: boolean;
  accessCode: string;
  isAccessCodeInUrl: boolean;
}

const PublicShareCard: React.FC<Props> = (props) => {
  const { requiresPasscode, languageCode, hash, accessCode, isAccessCodeInUrl } = props;
  const shareCardData = props.metadata;
  const metadata = props.metadata.metadata;

  const [, uiActions] = useUIState();


  React.useEffect(() => {
    if (!metadata) { return; }
    const p = new ProfileModel(profile);
    const shareCardName = metadata.title.i18n[languageCode];
    const profileName = p.greetingName();
    if (profileName) {
      uiActions.setWindowTitlePhrase({ id: 'shareCardURL.pageTitleWithName.text', values: { string1: profileName, string2: shareCardName } });
    } else {
      uiActions.setWindowTitlePhrase({ id: 'shareCardURL.pageTitleWithoutName.text', values: { string1: shareCardName } });
    }
  }, [Boolean(metadata)]);

  if (!metadata) { return null; }

  const { r, g, b, a } = metadata.color.rgba;
  const backgroundColor = `rgba(${r},${g},${b},${a})`;
  const title = getLocalText(metadata.title, languageCode);
  const iconSrc = _.get(metadata, 'icon.url_light', null);

  const he = {};
  const hev = {};
  const trackables = shareCardData.trackables;

  const trackablesLookup = {
    ...trackables.latest.reduce((acc, v) => ({ ...acc, [v.id]: v }), {}),
    ...trackables.all.reduce((acc, v) => ({ ...acc, [v.id]: v }), {}),
  };
  const heLookup = Object.keys(shareCardData.health_entries).reduce((acc, k) => {
    return {
      ...acc,
      ...shareCardData.health_entries[k].reduce((acc1, v) => ({ ...acc1, [v.id]: v }), {}),
    };
  }, {});

  const entryTrackables = shareCardData.entry_trackables;
  // const terms = shareCardData.trackable_terms[languageCode];
  const terms = {};

  Object.keys(shareCardData.health_entries).forEach(k => {
    he[k] = shareCardData.health_entries[k].map(h => generalHealthEntry(h, languageCode));
  });

  Object.keys(shareCardData.health_entry_values).forEach(k => {
    hev[k] = shareCardData.health_entry_values[k].map(h => generalHealthEntry(h, languageCode));
  });

  const healthEntries = healthEntryUtility.sortGroupedHealthEntriesByTime(he);
  const healthEntryValues = healthEntryUtility.sortGroupedHealthEntriesByTime(hev);

  const { profile, contacts, providers, files, notes, comment, group_contents, groups } = shareCardData;

  const colorModel = new ShareCardColorModel(metadata.color.rgba);
  return (
    <div className="page-publicsharecard" key={languageCode}>
      <ScrollAwareContext>

        <ScrollAwareScroller>

          <ScrollAwareItem isSticky hasDropShadow>
            <div className="page-publicsharecard-header" style={{ backgroundColor }}>

              <div className="page-publicsharecard-header_content util-public-share-card-frame-padding">
                <div>{title}</div>

                {iconSrc && <I18nImage data={iconSrc} height="48"/>}
              </div>
            </div>
          </ScrollAwareItem>

          <div className="page-publicsharecard-content">

            <PublicShareCardControls
              languageCode={languageCode}
              hash={hash}
              requiresPasscode={requiresPasscode}
            />

            <ShareCardProfileGroup
              profile={profile}
            />

            <ShareCardGroupContents
              languageCode={languageCode}
              groupContents={group_contents}
              groups={groups}
              colorModel={colorModel}
            />

            <ShareCardHealthEntryAllergyGroup
              category={HealthEntryCategoryNames.allergy}
              healthEntries={healthEntries[HealthEntryCategoryNames.allergy].all.map(a => a.data())}
              trackablesLookup={trackablesLookup}
              entryTrackables={entryTrackables}
              languageCode={languageCode}
              hasPeriodTitle={true}
            />

            <ShareCardHealthEntryGroup
              category={HealthEntryCategoryNames.condition}
              healthEntries={healthEntries[HealthEntryCategoryNames.condition].all.map(a => a.data())}
              trackablesLookup={trackablesLookup}
              entryTrackables={entryTrackables}
              languageCode={languageCode}
              hasPeriodTitle={true}
            />

            <ShareCardHealthEntryGroup
              category={HealthEntryCategoryNames.medication}
              healthEntries={healthEntries[HealthEntryCategoryNames.medication].all.map(a => a.data())}
              trackablesLookup={trackablesLookup}
              entryTrackables={entryTrackables}
              languageCode={languageCode}
              hasPeriodTitle={true}
            />

            <ShareCardHealthEntryGroup
              category={HealthEntryCategoryNames.otherTreatment}
              healthEntries={healthEntries[HealthEntryCategoryNames.otherTreatment].all.map(a => a.data())}
              trackablesLookup={trackablesLookup}
              entryTrackables={entryTrackables}
              languageCode={languageCode}
              hasPeriodTitle={true}
            />

            <ShareCardHealthEntryGroup
              category={HealthEntryCategoryNames.procedure}
              healthEntries={healthEntries[HealthEntryCategoryNames.procedure].all.map(a => a.data())}
              trackablesLookup={trackablesLookup}
              entryTrackables={entryTrackables}
              languageCode={languageCode}
              hasPeriodTitle={false}
            />

            <ShareCardHealthEntryGroup
              category={HealthEntryCategoryNames.vaccination}
              healthEntries={healthEntries[HealthEntryCategoryNames.vaccination].all.map(a => a.data())}
              trackablesLookup={trackablesLookup}
              entryTrackables={entryTrackables}
              languageCode={languageCode}
              hasPeriodTitle={false}
            />

            <ShareCardHealthEntryGroup
              category={HealthEntryCategoryNames.measurement}
              healthEntries={healthEntries[HealthEntryCategoryNames.measurement].all.map(a => a.data())}
              trackablesLookup={trackablesLookup}
              entryTrackables={entryTrackables}
              languageCode={languageCode}
              hasPeriodTitle={false}
            />

            <ShareCardHealthEntryGroup
              category={HealthEntryCategoryNames.lab}
              healthEntries={healthEntries[HealthEntryCategoryNames.lab].all.map(a => a.data())}
              trackablesLookup={trackablesLookup}
              entryTrackables={entryTrackables}
              languageCode={languageCode}
              hasPeriodTitle={false}
            />

            <ShareCardHealthEntryGroup
              category={HealthEntryCategoryNames.geneticResult}
              healthEntries={healthEntries[HealthEntryCategoryNames.geneticResult].all.map(a => a.data())}
              trackablesLookup={trackablesLookup}
              entryTrackables={entryTrackables}
              languageCode={languageCode}
              hasPeriodTitle={false}
            />

            <ShareCardTrackableGroup
              trackables={trackables}
              entryTrackables={entryTrackables}
              heLookup={heLookup}
              languageCode={languageCode}
              includeType={ShareCardIncludedTypeNames.latest}
            />

            <ShareCardHealthEntryValueGroup
              category={HealthEntryCategoryNames.measurement}
              healthEntries={healthEntryValues[HealthEntryCategoryNames.measurement].all.map(a => a.data())}
              languageCode={languageCode}
            />

            <ShareCardHealthEntryValueGroup
              category={HealthEntryCategoryNames.lab}
              healthEntries={healthEntryValues[HealthEntryCategoryNames.lab].all.map(a => a.data())}
              languageCode={languageCode}
            />

            <ShareCardHealthEntryValueGroup
              category={HealthEntryCategoryNames.geneticResult}
              healthEntries={healthEntryValues[HealthEntryCategoryNames.geneticResult].all.map(a => a.data())}
              languageCode={languageCode}
            />

            <ShareCardTrackableGroup
              trackables={trackables}
              entryTrackables={entryTrackables}
              heLookup={heLookup}
              languageCode={languageCode}
              includeType={ShareCardIncludedTypeNames.all}
            />

            <ShareCardContactsGroup
              isProvider={false}
              contacts={contacts}
            />

            <ShareCardContactsGroup
              isProvider={true}
              contacts={providers}
            />

            <ShareCardFilesGroup
              hash={hash}
              files={files}
              colorModel={colorModel}
              requiresPasscode={requiresPasscode}
              isAccessCodeInUrl={isAccessCodeInUrl}
              accessCode={accessCode}
            />

            <ShareCardNotesGroup
              notes={notes}
              languageCode={languageCode}
              colorModel={colorModel}
            />

            <ShareCardCommentGroup
              comment={comment}
              colorModel={colorModel}
            />

            <ShareCardDisclaimer />
          </div>

          <PublicShareCardFooter className="util-public-share-card-frame-padding"/>
        </ScrollAwareScroller>
      </ScrollAwareContext>
    </div>
  );
}

export default PublicShareCard;
